/**
 * TanStack Query hook for fetching story analysis data.
 *
 * Wraps analysisService.getAnalysis with caching and loading states.
 * Analysis is generated asynchronously by a background job after session
 * completion. When the backend returns 202 (processing), this hook polls
 * every 5 seconds until the analysis is ready.
 * @module useAnalysis
 */

import { useQuery } from '@tanstack/react-query'
import { analysisService } from '../services/analysisService'

export function useAnalysis(sessionId) {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['analysis', sessionId],
    queryFn: () => analysisService.getAnalysis(sessionId),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    refetchInterval: (query) => {
      // Poll every 5s while analysis is still processing (but not if upgrade needed)
      if (query.state.data?.status === 'processing') return 5000
      return false
    },
  })

  const isProcessing = data?.status === 'processing'
  const needsUpgrade = data?.status === 'upgrade_required'
  const analysis = (isProcessing || needsUpgrade) ? null : data

  return { analysis, isLoading, isProcessing, needsUpgrade, error, refetch }
}
